import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import styled from 'styled-components'
import WrappedVideoBackground from '../Components/WrappedVideoBackground'
import WrappedImageBackground from '../Components/WrappedImageBackground'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions, type WrappedStoryState } from 'contexts/Wrapped'
import { getVideoFallbackTimeout, VIDEO_FALLBACK_TIMEOUT_REDUCED_MOTION_MS } from '../config/slides'
import useSlowConnectionDetection from './useSlowConnectionDetection'
import type { SlideConfig, SlideId } from '../types/wrapped'

/**
 * Backup timeout to assume video is ready if events don't fire (e.g., cached videos).
 * Must be shorter than the minimum fallback timeout (VIDEO_FALLBACK_TIMEOUT_REDUCED_MOTION_MS)
 * to avoid race conditions.
 */
const BACKUP_VIDEO_READY_TIMEOUT_MS = Math.min(300, VIDEO_FALLBACK_TIMEOUT_REDUCED_MOTION_MS - 100)

interface UseVideoBackgroundWithFallbackOptions {
  /** The slide configuration containing video and fallback image info */
  slideConfig: SlideConfig | undefined
  /** The slide ID to check if this is the current slide */
  slideId: SlideId
}

interface UseVideoBackgroundWithFallbackResult {
  /** The background element to render (video or image) */
  background: React.ReactNode | undefined
  /** Whether the background is ready and content can animate */
  isBackgroundReady: boolean
  /** Whether this is the current slide */
  isCurrentSlide: boolean
}

/**
 * Container for layering image and video backgrounds
 */
const BackgroundLayer = styled.div`
  position: absolute;
  inset: 0;
`

/**
 * Video layer that fades in when ready
 */
const VideoLayer = styled(BackgroundLayer)`
  opacity: 0;
  transition: opacity 0.4s ease-in-out;
  /* Ensure video is above image when visible */
  z-index: 1;

  &.is-visible {
    opacity: 1;
  }
`

/**
 * Image layer shown immediately as base
 */
const ImageLayer = styled(BackgroundLayer)`
  z-index: 0;
`

/**
 * Hook that manages video background with progressive enhancement.
 *
 * Strategy: "Image-first, video-overlay"
 * 1. Show the fallback image immediately for instant visual feedback
 * 2. Load the video in the background (invisible)
 * 3. When video is ready, crossfade from image to video
 * 4. If video doesn't load in time, keep showing the image
 *
 * This provides the best perceived performance while still allowing
 * videos to enhance the experience when they load quickly.
 */
// Memoized selectors to prevent unnecessary re-renders (as recommended by useWrappedStorySelector)
const selectIsPaused = (state: WrappedStoryState) => state.isPaused

function useVideoBackgroundWithFallback({
  slideConfig,
  slideId,
}: UseVideoBackgroundWithFallbackOptions): UseVideoBackgroundWithFallbackResult {
  // Memoize selector with slideId dependency to maintain stable reference
  const selectIsCurrentSlide = useCallback(
    (state: WrappedStoryState) => state.currentSlide?.id === slideId,
    [slideId],
  )
  const isCurrentSlide = useWrappedStorySelector(selectIsCurrentSlide)
  const isPaused = useWrappedStorySelector(selectIsPaused)
  const dispatch = useWrappedStoryDispatch()

  // Detect slow connections and Data Saver mode
  const connectionInfo = useSlowConnectionDetection()
  const fallbackTimeout = getVideoFallbackTimeout(connectionInfo)

  // Track if the video has actually loaded (via callback from video component)
  const [videoActuallyReady, setVideoActuallyReady] = useState(false)
  // Track if we should show the video (it loaded successfully)
  // Once true, stays true for the duration of the slide
  const [showVideo, setShowVideo] = useState(false)
  // Track if we've given up trying to load the video (timeout expired before video ready)
  const [videoTimedOut, setVideoTimedOut] = useState(false)

  const video = slideConfig?.video
  const fallbackImageId = slideConfig?.fallbackImageId
  const isFirstSlide = slideId === 'intro'

  // Callback when image loads - dispatch first slide image ready for the intro slide
  const handleImageLoad = useCallback(() => {
    if (isFirstSlide) {
      dispatch({ type: WrappedStoryActions.setFirstSlideImageReady, isFirstSlideImageReady: true })
    }
  }, [isFirstSlide, dispatch])

  // Skip video entirely if timeout is 0 (Data Saver or very slow connection)
  const shouldSkipVideo = fallbackTimeout === 0

  // Track previous isCurrentSlide to detect transitions
  const wasCurrentSlideRef = useRef(false)
  // Track if video has started showing (to prevent timeout from hiding it)
  const hasShownVideoRef = useRef(false)
  // Track if this is the first activation (initial mount)
  const isFirstActivationRef = useRef(true)
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true)
  // Track if video was ever successfully shown (for cache optimization)
  const videoEverShownRef = useRef(false)

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Callback to receive video ready state from WrappedVideoBackground
  // Once video is reported ready, keep it as ready (ignore subsequent false reports
  // which can happen during brief buffering moments)
  // Guards against state updates after unmount from async Vimeo player events
  const handleVideoReadyChange = useCallback((isReady: boolean) => {
    if (isReady && isMountedRef.current) {
      setVideoActuallyReady(true)
    }
  }, [])

  // Reset state when slide changes (leaving or transitioning between slides)
  // Don't reset on initial mount - state is already initialized correctly
  // Skip reset if video was previously shown (keeps video cached for instant display on return)
  useEffect(() => {
    const becameActive = isCurrentSlide && !wasCurrentSlideRef.current
    const wasActive = wasCurrentSlideRef.current

    if (!isCurrentSlide && wasActive) {
      // When leaving the slide: only reset UI state, preserve video ready state if video was shown
      // This allows the video layer to hide but stay ready for instant re-display on return
      setShowVideo(false)
      hasShownVideoRef.current = false
      // Don't reset videoActuallyReady or videoTimedOut if video was ever shown
      // This preserves the cached video state
      if (!videoEverShownRef.current) {
        setVideoTimedOut(false)
        setVideoActuallyReady(false)
      }
    } else if (becameActive && !isFirstActivationRef.current) {
      // Returning to this slide
      if (videoEverShownRef.current) {
        // Video was previously shown - immediately show it again without reset/fade
        setShowVideo(true)
        hasShownVideoRef.current = true
      } else {
        // Video was never shown - reset for fresh attempt
        setShowVideo(false)
        setVideoTimedOut(false)
        setVideoActuallyReady(false)
        hasShownVideoRef.current = false
      }
    }

    if (becameActive) {
      isFirstActivationRef.current = false
    }

    wasCurrentSlideRef.current = isCurrentSlide
  }, [isCurrentSlide])

  // Dispatch isVideoReady: true when using image-first approach
  // This allows the story timer to start since image is shown right away
  // WrappedStoryContent now skips its isVideoReady dispatch for slides with fallbackImageId,
  // so we can safely dispatch here without race conditions
  useEffect(() => {
    if (isCurrentSlide && video && fallbackImageId) {
      dispatch({ type: WrappedStoryActions.setVideoReady, isVideoReady: true })
    }
  }, [isCurrentSlide, video, fallbackImageId, dispatch])

  // Show video when it's actually ready and we haven't timed out
  useEffect(() => {
    if (videoActuallyReady && !videoTimedOut) {
      setShowVideo(true)
      hasShownVideoRef.current = true
      videoEverShownRef.current = true
    }
  }, [videoActuallyReady, videoTimedOut])

  // Backup mechanism: If video is mounted and slide is active,
  // assume ready after a short timeout. This handles edge cases where
  // video events might not fire correctly (e.g., cached videos).
  // Uses BACKUP_VIDEO_READY_TIMEOUT_MS which is guaranteed to be shorter than
  // the minimum fallbackTimeout to avoid race conditions.
  useEffect(() => {
    if (!isCurrentSlide || !video || shouldSkipVideo || !fallbackImageId) {
      return
    }

    // If video is already showing, don't need backup
    if (showVideo) return

    // Skip backup if fallback timeout is too short (would race with it)
    if (fallbackTimeout <= BACKUP_VIDEO_READY_TIMEOUT_MS) {
      return
    }

    const backupTimeoutId = setTimeout(() => {
      // Only trigger if video hasn't been shown yet, we haven't timed out, and still mounted
      if (!hasShownVideoRef.current && !videoTimedOut && isMountedRef.current) {
        setVideoActuallyReady(true)
      }
    }, BACKUP_VIDEO_READY_TIMEOUT_MS)

    return () => clearTimeout(backupTimeoutId)
  }, [isCurrentSlide, video, shouldSkipVideo, fallbackImageId, showVideo, videoTimedOut, fallbackTimeout])

  // Start timeout when slide becomes active
  useEffect(() => {
    // Skip if not current slide, no video, or should skip video entirely
    if (!isCurrentSlide || !video || shouldSkipVideo) {
      return
    }

    // Set a timeout - if video doesn't load by then, stop trying to load it
    // But don't hide video if it's already showing
    const timeoutId = setTimeout(() => {
      if (!hasShownVideoRef.current) {
        setVideoTimedOut(true)
      }
    }, fallbackTimeout)

    return () => clearTimeout(timeoutId)
  }, [isCurrentSlide, video, shouldSkipVideo, fallbackTimeout])

  // Background is ready immediately if we have a fallback image to show
  // This is independent of whether the video has loaded
  const isBackgroundReady = useMemo(() => {
    // No video config means we're ready immediately
    if (!video) return true
    // If we have a fallback image, we're ready immediately (image-first approach)
    if (fallbackImageId) return true
    // No fallback image - must wait for video (check context state)
    // For video-only slides, WrappedVideoBackground handles the context dispatch
    return videoActuallyReady
  }, [video, fallbackImageId, videoActuallyReady])

  // Create the layered background element
  const background = useMemo(() => {
    if (!video) return undefined

    // Skip video entirely on slow connections - just show image
    if (shouldSkipVideo && fallbackImageId) {
      return (
        <WrappedImageBackground
          aspectRatio="9:16"
          gradientOverlay={false}
          imageId={fallbackImageId}
          kenBurnsVariant="zoom"
          isActive={isCurrentSlide && !isPaused}
          onLoad={handleImageLoad}
        />
      )
    }

    // No fallback image - just show video (old behavior, video controls context)
    if (!fallbackImageId) {
      return <WrappedVideoBackground video={video} isActive={isCurrentSlide} />
    }

    // Progressive enhancement: Image base layer + Video overlay
    // Video fades in when ready, providing smooth transition
    // Once video is showing, keep it visible and mounted
    const shouldLoadVideo = !videoTimedOut || showVideo
    const shouldShowVideo = showVideo

    return (
      <>
        {/* Base layer: Image shown immediately */}
        <ImageLayer>
          <WrappedImageBackground
            aspectRatio="9:16"
            gradientOverlay={false}
            imageId={fallbackImageId}
            kenBurnsVariant={videoTimedOut && !showVideo ? 'zoom' : undefined}
            isActive={isCurrentSlide && !isPaused}
            onLoad={handleImageLoad}
          />
        </ImageLayer>

        {/* Overlay layer: Video loads in background, fades in when ready */}
        {shouldLoadVideo && (
          <VideoLayer className={shouldShowVideo ? 'is-visible' : undefined}>
            <WrappedVideoBackground
              video={video}
              isActive={isCurrentSlide}
              suppressContextDispatch
              onVideoReadyChange={handleVideoReadyChange}
              transparentBackground
            />
          </VideoLayer>
        )}
      </>
    )
  }, [video, shouldSkipVideo, fallbackImageId, isCurrentSlide, isPaused, videoTimedOut, showVideo, handleVideoReadyChange, handleImageLoad])

  return {
    background,
    isBackgroundReady,
    isCurrentSlide,
  }
}

export default useVideoBackgroundWithFallback
